import React, { useState, useEffect } from "react";
import { getFirestore, collection, query as queryFirestore, where, orderBy, limit, getDocs } from 'firebase/firestore';
import Container from '@mui/material/Container';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import moment from "moment";
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { StyledTableCell, StyledTableRow } from '../../utils/styled'
import { Button, CircularProgress, TextField, FormControlLabel, Switch, Box } from "@mui/material";

const firestore = getFirestore();

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function parseFechaToYYYYMMDD(fecha) {
    if (!fecha) return "";
    const str = String(fecha).trim();

    if (/^\d{8}$/.test(str)) return str;
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10).replaceAll("-", "");

    const parsed = moment(str, ["DD/MM/YYYY HH:mm", "DD/MM/YYYY", "YYYYMMDD", moment.ISO_8601], true);
    return parsed.isValid() ? parsed.format("YYYYMMDD") : "";
}

function SerialesSearch() {
    const [searchValue, setSearchValue] = useState('');
    const [searchResult, setSearchResult] = useState([]);
    const [snackbarInfo, setSnackbarInfo] = useState({ text: '', type: '', open: false });
    const [loading, setLoading] = useState(false);

    const currentYear = new Date().getFullYear();
    const [advancedSearch, setAdvancedSearch] = useState(false);
    const [fechaDesde, setFechaDesde] = useState(`${currentYear}-01-01`);
    const [fechaHasta, setFechaHasta] = useState(`${currentYear}-12-31`);

    useEffect(() => {
        // No initial load to optimize performance
    }, []);

    const handleClose = () => {
        setSnackbarInfo({ text: '', type: '', open: false });
    }

    const handleCloseSanckbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackbarInfo({ text: '', type: '', open: false });
    };

    // Función para normalizar fechas y convertir a timestamp para ordenamiento
    const normalizeDateForSorting = (fecha, type) => {
        if (!fecha) return 0;
        
        try {
            let momentDate;
            
            if (type === 'remito') {
                // Remitos pueden venir en formato DD/MM/YYYY o YYYYMMDD
                if (fecha.includes('/')) {
                    // Formato DD/MM/YYYY
                    momentDate = moment(fecha, "DD/MM/YYYY");
                } else if (fecha.length === 8) {
                    // Formato YYYYMMDD
                    momentDate = moment(fecha, "YYYYMMDD");
                } else {
                    // Intentar parsear automáticamente
                    momentDate = moment(fecha);
                }
            } else if (type === 'orden') {
                // Órdenes siempre vienen en formato YYYYMMDD
                momentDate = moment(fecha, "YYYYMMDD");
            } else {
                // Fallback: intentar parsear automáticamente
                momentDate = moment(fecha);
            }
            
            // Verificar que la fecha sea válida
            if (!momentDate.isValid()) {
                console.warn(`Fecha inválida: ${fecha} (tipo: ${type})`);
                return 0;
            }
            
            return momentDate.valueOf(); // Retorna timestamp para ordenamiento
            
        } catch (error) {
            console.warn(`Error normalizando fecha: ${fecha} (tipo: ${type})`, error);
            return 0;
        }
    };

    // Función para ordenar resultados por fecha (más reciente primero)
    const sortResultsByDate = (results) => {
        console.log('🔄 Iniciando ordenamiento por fecha...');
        
        const sortedResults = results.sort((a, b) => {
            const timestampA = normalizeDateForSorting(a.fecha, a.type);
            const timestampB = normalizeDateForSorting(b.fecha, b.type);
            
            // Ordenamiento descendente (más reciente primero)
            return timestampB - timestampA;
        });
        
        console.log('✅ Ordenamiento completado');
        return sortedResults;
    };

    const searchFilter = async () => {
        if (!searchValue.trim()) {
            setSnackbarInfo({ text: 'Por favor ingrese un serial para buscar', type: 'warning', open: true });
            return;
        }

        setLoading(true);
        setSearchResult([]); // Limpiar resultados previos
        
        try {
            const ordenesCollection = collection(firestore, "ordenes");
            const remitosCollection = collection(firestore, "remitos");

            const ordenesQueryOnt = queryFirestore(ordenesCollection, where("serialOnt", "array-contains", searchValue));
            const ordenesQueryFlow = queryFirestore(ordenesCollection, where("serialesFlow", "array-contains", searchValue));
            const ordenesQueryOntR = queryFirestore(ordenesCollection, where("ontRetirado", "array-contains", searchValue));
            const ordenesQueryFlowR = queryFirestore(ordenesCollection, where("decoRetirado", "array-contains", searchValue));

            console.log('Iniciando búsqueda paralela en remitos y órdenes...');

            // Ejecuta todas las consultas en paralelo
            const [
                remitosSnapshot,
                ordenesOntSnapshot,
                ordenesFlowSnapshot,
                ordenesOntRSnapshot,
                ordenesFlowRSnapshot
            ] = await Promise.all([
                getDocs(queryFirestore(remitosCollection, orderBy('id', 'desc'), limit(10000))),
                getDocs(ordenesQueryOnt),
                getDocs(ordenesQueryFlow),
                getDocs(ordenesQueryOntR),
                getDocs(ordenesQueryFlowR),
            ]);

            console.log('Búsqueda paralela completada. Procesando resultados...');

            // Formatear fechas de búsqueda para filtro
            const desde = fechaDesde ? fechaDesde.replaceAll("-", "") : "";
            const hasta = fechaHasta ? fechaHasta.replaceAll("-", "") : "";

            // Procesar remitos
            const remitosResults = [];
            if (!remitosSnapshot.empty) {
                const remitosList = remitosSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
                remitosList.forEach(remito => {
                    // Validar rango de fecha
                    const fechaNorm = parseFechaToYYYYMMDD(remito.fecha);
                    if (desde && fechaNorm && fechaNorm < desde) return;
                    if (hasta && fechaNorm && fechaNorm > hasta) return;

                    // Validar serial
                    const serialFilter = remito.serials?.filter(serial => String(serial?.id || '').trim().toLowerCase() === searchValue.trim().toLowerCase());
                    if (serialFilter?.length > 0) {
                        const formatResult = {
                            id: remito.id,
                            fecha: remito.fecha, // Mantener formato original para ordenamiento
                            fechaFormateada: remito.fecha.includes('/') ? remito.fecha : moment(remito.fecha, "YYYYMMDD").format("DD/MM/YYYY"), // Para visualización
                            cuadrillaEntrega: remito.cuadrillaEntrega,
                            cuadrillaReceptora: remito.cuadrillaReceptora,
                            tipoRemito: remito.tipoRemito,
                            type: 'remito',
                            serial: searchValue
                        };
                        remitosResults.push(formatResult);
                    }
                });
            }

            console.log('Resultados de remitos:', remitosResults.length);

            // Procesar órdenes
            const allDocs = [
                ...ordenesOntSnapshot.docs,
                ...ordenesFlowSnapshot.docs,
                ...ordenesOntRSnapshot.docs,
                ...ordenesFlowRSnapshot.docs,
            ];

            // Eliminar duplicados basados en el ID del documento
            const uniqueDocs = allDocs.filter((doc, index, self) => 
                index === self.findIndex(d => d.id === doc.id)
            );

            const ordenesResults = [];
            uniqueDocs.forEach(doc => {
                const orderData = doc.data();
                
                // Validar rango de fecha
                const fechaNorm = parseFechaToYYYYMMDD(orderData.date);
                if (desde && fechaNorm && fechaNorm < desde) return;
                if (hasta && fechaNorm && fechaNorm > hasta) return;

                ordenesResults.push({
                    id: orderData.nroOt,
                    type: 'orden',
                    serial: searchValue,
                    fecha: orderData.date, // Mantener formato original YYYYMMDD para ordenamiento
                    fechaFormateada: moment(orderData.date, "YYYYMMDD").format("DD/MM/YYYY"), // Para visualización
                    cuadrillaEntrega: orderData.cuadrilla,
                    cuadrillaReceptora: '-',
                    tipoRemito: orderData.tipoOt,
                });
            });

            console.log('Resultados de órdenes:', ordenesResults.length);

            // Combinar todos los resultados
            const allResults = [...remitosResults, ...ordenesResults];

            // Ordenar resultados por fecha (más reciente primero)
            const sortedResults = sortResultsByDate(allResults);
            console.log('Resultados ordenados:', sortedResults.length);

            setSearchResult(sortedResults);
        } catch (error) {
            console.error('Error en la búsqueda:', error);
            setSnackbarInfo({ text: 'Error al realizar la búsqueda', type: 'error', open: true });
        } finally {
            setLoading(false);
        }
    };

    const onChangeSearch = (e) => {
        setSearchValue(e.target.value)
    }

    const onKeyPress = (e) => {
        if (e.keyCode === 13) {
            searchFilter();
        }
    }


    const displayTable = searchResult;

    return (
        <Container component="main" maxWidth="xl" sx={{ marginTop: 2, marginBottom: 4 }}>
            <Typography variant="h5" sx={{ marginBottom: 2 }}>Buscador de Seriales</Typography>
            <div className="filters-container" style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '20px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                    <Paper
                        sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', width: 400 }}
                    >
                        <InputBase
                            sx={{ ml: 1, flex: 1 }}
                            placeholder="Buscar serial"
                            inputProps={{ 'aria-label': 'buscar' }}
                            onKeyDown={onKeyPress}
                            onChange={onChangeSearch}
                        />
                        <IconButton type="button" sx={{ p: '10px' }} aria-label="search" onClick={searchFilter}>
                            <SearchIcon />
                        </IconButton>
                    </Paper>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={advancedSearch}
                                onChange={(e) => {
                                    setAdvancedSearch(e.target.checked);
                                    if (!e.target.checked) {
                                        setFechaDesde(`${currentYear}-01-01`);
                                        setFechaHasta(`${currentYear}-12-31`);
                                    }
                                }}
                                color="primary"
                            />
                        }
                        label="Búsqueda avanzada (Filtro de fecha)"
                    />
                    <MuiAlert severity="info" sx={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                        Búsqueda optimizada - Obtiene resultados de remitos y órdenes en una sola consulta (Limitada al año corriente por defecto)
                    </MuiAlert>
                </Box>

                {advancedSearch && (
                    <Box sx={{ display: 'flex', gap: '20px', flexWrap: 'wrap', p: 2, border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#fcfcfc', maxWidth: 'fit-content' }}>
                        <TextField
                            label="Fecha desde"
                            type="date"
                            size="small"
                            value={fechaDesde}
                            onChange={(e) => setFechaDesde(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Fecha hasta"
                            type="date"
                            size="small"
                            value={fechaHasta}
                            onChange={(e) => setFechaHasta(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Box>
                )}
            </div>
            <TableContainer component={Paper}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>ID/Nº OT</StyledTableCell>
                            <StyledTableCell>Fecha</StyledTableCell>
                            <StyledTableCell>Nº Serial</StyledTableCell>
                            <StyledTableCell>Tipo</StyledTableCell>
                            <StyledTableCell>Almacén origen</StyledTableCell>
                            <StyledTableCell>Almacén destino</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {searchResult.length > 0 ? (
                            displayTable.map((remito, index) => {
                                const linkSearch = remito.type === 'orden' ? `/list-ordenes?nroOt=${remito.id}` :
                                    `/remito-list?idRemito=${remito.id}`
                                return (
                                    <StyledTableRow
                                        key={index}
                                        sx={remito?.deleted && { display: 'none' }}
                                    >
                                        <StyledTableCell>
                                            <Button variant="text" href={linkSearch}>{remito.id}</Button>
                                        </StyledTableCell>
                                        <StyledTableCell>{remito.fechaFormateada || remito.fecha}</StyledTableCell>
                                        <StyledTableCell>{remito.serial}</StyledTableCell>
                                        <StyledTableCell>{remito.tipoRemito?.toUpperCase()}</StyledTableCell>
                                        <StyledTableCell>{remito.cuadrillaEntrega}</StyledTableCell>
                                        <StyledTableCell>{remito.cuadrillaReceptora}</StyledTableCell>
                                    </StyledTableRow>
                                )
                            })
                        ) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={13} align="center">
                                    {loading ? (
                                        <CircularProgress />

                                    ) : (
                                        <Typography variant="h5">
                                            No se encontraron elementos
                                        </Typography>

                                    )}
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Snackbar open={snackbarInfo.open} autoHideDuration={2000} onClose={handleCloseSanckbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleClose} severity={snackbarInfo.type} sx={{ width: '100%' }}>
                    {snackbarInfo.text}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default SerialesSearch;
